import React, { useEffect, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line,
} from "recharts";
import { BarChart3, Download, TrendingUp, Clock, Award, Mail } from "lucide-react";
import { toast } from "sonner";

import { v2Api } from "@/lib/v2Api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const DIMS = [
  { id: "client_type", label: "Client type" },
  { id: "state", label: "State" },
  { id: "industry", label: "Industry" },
  { id: "value_bucket", label: "Tender value" },
  { id: "year", label: "Year" },
  { id: "loss_category", label: "Loss reason" },
];

function ExportLinks({ csvUrl, pdfUrl }) {
  return (
    <div className="flex gap-3">
      <a href={csvUrl} className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
        <Download className="w-3.5 h-3.5" /> CSV
      </a>
      <a href={pdfUrl} className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
        <Download className="w-3.5 h-3.5" /> PDF
      </a>
    </div>
  );
}

function WinLossSection({ data }) {
  const [dim, setDim] = useState("client_type");
  if (!data) return <p className="text-sm text-slate-500">Loading…</p>;

  const dimMap = {
    client_type: data.by_client_type,
    state: data.by_state,
    industry: data.by_industry,
    value_bucket: data.by_value_bucket,
    year: data.by_year,
    loss_category: data.by_loss_category,
  };
  const rows = (dimMap[dim] || []).slice(0, 12);

  return (
    <Card>
      <CardHeader className="pb-2 flex-row flex items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-blue-600" /> Win / Loss analysis
        </CardTitle>
        <ExportLinks
          csvUrl={v2Api.reports.exportWinLossUrl(dim)}
          pdfUrl={v2Api.reports.exportWinLossPdfUrl()}
        />
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-1">
          {DIMS.map((d) => (
            <button
              key={d.id}
              onClick={() => setDim(d.id)}
              className={`text-xs px-2 py-1 rounded-full border ${
                dim === d.id
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {d.label}
            </button>
          ))}
        </div>
        {rows.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">
            No historical bids in this slice yet. Add or import bids on the
            Historical Bids page.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={rows} margin={{ top: 10, right: 20, bottom: 30, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="key" angle={-25} textAnchor="end" interval={0} height={70} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="won" stackId="a" fill="#10b981" />
              <Bar dataKey="lost" stackId="a" fill="#ef4444" />
              <Bar dataKey="other" stackId="a" fill="#94a3b8" />
            </BarChart>
          </ResponsiveContainer>
        )}
        <div className="text-xs text-slate-500 grid grid-cols-2 md:grid-cols-4 gap-2 pt-2 border-t">
          <p>
            Overall: <span className="font-mono">{data.overall.won}W / {data.overall.lost}L</span>
          </p>
          <p>
            Win rate: <span className="font-mono">{data.overall.win_rate_pct}%</span>
          </p>
          <p>
            Won value: <span className="font-mono">
              ₹{(data.overall.won_value_inr / 1e7).toFixed(2)} Cr
            </span>
          </p>
          <p>
            Lost value: <span className="font-mono">
              ₹{(data.overall.lost_value_inr / 1e7).toFixed(2)} Cr
            </span>
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function BoqAccuracySection({ data }) {
  if (!data) return null;
  return (
    <Card>
      <CardHeader className="pb-2 flex-row flex items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-blue-600" /> BOQ accuracy — price gap vs win rate
        </CardTitle>
        <ExportLinks csvUrl={v2Api.reports.exportBoqUrl()} pdfUrl={v2Api.reports.exportBoqPdfUrl()} />
      </CardHeader>
      <CardContent className="space-y-3">
        {data.sample_size === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">
            No bids with our_price_gap_pct set yet. Populate the field on Historical Bids
            (or via bulk import) to see this report.
          </p>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.rows}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="gap_bucket" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="won" fill="#10b981" />
                <Bar dataKey="lost" fill="#ef4444" />
                <Line dataKey="win_rate_pct" type="monotone" stroke="#2563eb" />
              </BarChart>
            </ResponsiveContainer>
            <div className="text-xs text-slate-500 grid grid-cols-3 gap-2 pt-2 border-t">
              <p>Sample size: <span className="font-mono">{data.sample_size}</span></p>
              <p>Median gap (won): <span className="font-mono">{data.median_gap_won_pct ?? "—"}%</span></p>
              <p>Median gap (lost): <span className="font-mono">{data.median_gap_lost_pct ?? "—"}%</span></p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function BenchmarkRow({ slice }) {
  if (!slice) return null;
  const cls = (() => {
    if (slice.you_pct == null || slice.market_median_pct == null) return "text-slate-400";
    if (slice.you_pct >= slice.market_median_pct) return "text-emerald-700 font-semibold";
    return "text-amber-700 font-semibold";
  })();
  return (
    <tr className="border-t">
      <td className="py-1 text-slate-600">{slice.key || "—"}</td>
      <td className={`py-1 text-right font-mono ${cls}`}>
        {slice.you_pct != null ? `${slice.you_pct.toFixed(1)}%` : "—"}
        {slice.you_sample > 0 && <span className="text-[10px] text-slate-400 ml-1">({slice.you_sample})</span>}
      </td>
      <td className="py-1 text-right text-slate-700 font-mono">
        {slice.insufficient_sample
          ? <span className="text-[10px] italic text-slate-400">need ≥ 3 ws</span>
          : (slice.market_median_pct != null
              ? `${slice.market_p25_pct?.toFixed(1)}%–${slice.market_p75_pct?.toFixed(1)}% / med ${slice.market_median_pct.toFixed(1)}%`
              : "—")}
        {slice.market_workspaces > 0 && (
          <span className="text-[10px] text-slate-400 ml-1">({slice.market_workspaces} ws)</span>
        )}
      </td>
    </tr>
  );
}

function BenchmarksSection({ data }) {
  if (!data) return null;
  if (data.overall?.insufficient_sample && (!data.by_client_type || data.by_client_type.length === 0)) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-violet-600" /> Cross-tenant benchmarks
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 italic">
            Not enough other workspaces in the system yet to compute privacy-safe
            benchmarks (k-anonymity floor of 3). This will populate as the
            customer base grows.
          </p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-violet-600" /> Cross-tenant benchmarks
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-slate-500">
          Win-rate vs the market. Anonymised — only aggregates with ≥ 3 other
          workspaces are shown.
        </p>
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
            <tr>
              <th className="text-left py-1">Slice</th>
              <th className="text-right py-1">You</th>
              <th className="text-right py-1">Market p25 / median / p75</th>
            </tr>
          </thead>
          <tbody>
            <BenchmarkRow slice={data.overall} />
            {data.by_client_type?.length > 0 && (
              <>
                <tr><td colSpan={3} className="pt-2 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">By client type</td></tr>
                {data.by_client_type.map((s) => <BenchmarkRow key={`ct-${s.key}`} slice={s} />)}
              </>
            )}
            {data.by_industry_sector?.length > 0 && (
              <>
                <tr><td colSpan={3} className="pt-2 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">By industry sector</td></tr>
                {data.by_industry_sector.map((s) => <BenchmarkRow key={`is-${s.key}`} slice={s} />)}
              </>
            )}
            {data.by_tender_portal?.length > 0 && (
              <>
                <tr><td colSpan={3} className="pt-2 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">By portal</td></tr>
                {data.by_tender_portal.map((s) => <BenchmarkRow key={`pt-${s.key}`} slice={s} />)}
              </>
            )}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function RoiSection({ data }) {
  if (!data) return null;
  const fmtCr = (n) => (n == null ? "—" : `₹${(n / 1e7).toFixed(2)} Cr`);
  return (
    <Card>
      <CardHeader className="pb-2 flex-row flex items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Award className="w-4 h-4 text-emerald-600" /> ROI — {data.period_label}
        </CardTitle>
        <a
          href={v2Api.reports.exportRoiPdfUrl()}
          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
        >
          <Download className="w-3.5 h-3.5" /> PDF for the CXO
        </a>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="bg-gradient-to-r from-emerald-50 to-blue-50 border rounded-md p-3">
          <p className="text-xl font-semibold text-blue-900">{data.headline_top}</p>
          <p className="text-sm text-slate-600 mt-1">{data.headline_bottom}</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          {[
            ["RFPs analysed", data.rfps_analysed],
            ["Bids submitted", data.bids_submitted],
            ["Won / Lost", `${data.bids_won} / ${data.bids_lost}`],
            ["Win rate", data.win_rate_pct != null ? `${data.win_rate_pct} %` : "—"],
            ["Pipeline", fmtCr(data.pipeline_value_inr)],
            ["Won value", fmtCr(data.won_value_inr)],
            ["BoQ lines auto-matched", `${data.boq_lines_auto_matched} / ${data.boq_lines_imported}`],
            ["Hours saved", `${data.estimated_hours_saved.toLocaleString("en-IN")} h`],
          ].map(([k, v]) => (
            <div key={k} className="bg-white border rounded p-2">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{k}</p>
              <p className="font-semibold text-slate-800 mt-0.5">{v}</p>
            </div>
          ))}
        </div>
        {(data.top_clients || []).length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">
              Top clients this quarter
            </p>
            <ul className="text-xs space-y-0.5">
              {data.top_clients.slice(0, 5).map((c) => (
                <li key={c.client} className="flex items-center justify-between">
                  <span className="text-slate-700">{c.client}</span>
                  <span className="text-slate-500 font-mono">
                    {c.submissions} bids · {fmtCr(c.value_inr)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {data.win_rate_delta_pct != null && (
          <p className="text-[11px] text-slate-500">
            <strong className={data.win_rate_delta_pct >= 0 ? "text-emerald-700" : "text-rose-700"}>
              {data.win_rate_delta_pct >= 0 ? "▲" : "▼"} {Math.abs(data.win_rate_delta_pct)} pp
            </strong>{" "}
            vs {data.prev_period_label}.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function CycleTimeSection({ data }) {
  if (!data) return null;
  return (
    <Card>
      <CardHeader className="pb-2 flex-row flex items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Clock className="w-4 h-4 text-blue-600" /> Cycle time — average days per stage
        </CardTitle>
        <ExportLinks csvUrl={v2Api.reports.exportCycleUrl()} pdfUrl={v2Api.reports.exportCyclePdfUrl()} />
      </CardHeader>
      <CardContent>
        {data.transitions.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">
            No bid stage transitions yet. Once bids start flowing through the GeM stages
            this report will populate automatically.
          </p>
        ) : (
          <>
            <table className="w-full text-xs">
              <thead className="text-slate-500">
                <tr>
                  <th className="text-left py-1">From</th>
                  <th className="text-left py-1">To</th>
                  <th className="text-right py-1">Avg days</th>
                  <th className="text-right py-1">Median</th>
                  <th className="text-right py-1">Samples</th>
                </tr>
              </thead>
              <tbody>
                {data.transitions.slice(0, 20).map((t, i) => (
                  <tr key={i} className="border-t">
                    <td className="py-1 font-mono">{t.from_stage}</td>
                    <td className="py-1 font-mono">{t.to_stage}</td>
                    <td className="py-1 text-right font-mono">{t.avg_days}</td>
                    <td className="py-1 text-right font-mono">{t.median_days}</td>
                    <td className="py-1 text-right text-slate-500">{t.samples}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-slate-500 pt-2 border-t mt-2">
              Overall weighted average:{" "}
              <span className="font-mono">{data.overall_avg_days} days</span>
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function ReportsPage() {
  const [winLoss, setWinLoss] = useState(null);
  const [boq, setBoq] = useState(null);
  const [cycle, setCycle] = useState(null);
  const [roi, setRoi] = useState(null);
  const [benchmarks, setBenchmarks] = useState(null);

  useEffect(() => {
    Promise.all([
      v2Api.reports.winLoss().then(setWinLoss).catch(() => {}),
      v2Api.reports.boqAccuracy().then(setBoq).catch(() => {}),
      v2Api.reports.cycleTime().then(setCycle).catch(() => {}),
      v2Api.reports.roiQuarterly().then(setRoi).catch(() => {}),
      v2Api.pricing.benchmarks().then(setBenchmarks).catch(() => {}),
    ]).catch(() => toast.error("Could not load reports"));
  }, []);

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-blue-600" /> Reports
        </h1>
        <p className="text-sm text-slate-500">
          Win/loss analytics, BOQ pricing accuracy, and GeM cycle-time. All
          reports support CSV export — feed them straight into Excel or Power BI.
        </p>
      </div>

      <RoiSection data={roi} />
      <BenchmarksSection data={benchmarks} />
      <WinLossSection data={winLoss} />
      <BoqAccuracySection data={boq} />
      <CycleTimeSection data={cycle} />
    </div>
  );
}
